import {
  ArrowPathIcon,
  Breadcrumb,
  Button,
  CalendarDaysIcon,
  DownloadExcel,
  ErrorAlert,
  formatNumber,
  Modal,
  ModalOpenBtn,
  PageHero,
  SearchToggle,
  TableRowsPerPage,
  TruckIcon,
  XCircleIcon,
} from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { useDonationsPage } from "../hooks";
import { exportUnassignedVerificationStagesExcel } from "../api/api.ts";
import { donationsExcelHeaders } from "../lib/options.ts";
import { tableColumns } from "../components/TableColumns.tsx";
import MultiSearch from "../components/MultiSearch.tsx";
import AssignDriverModal from "../components/AssignDriverModal.tsx";
import ManualVerificationDropdown from "../components/ManualVerificationDropdown.tsx";
import UpdateSmsSendingDateModal, {
  UPDATE_SMS_SENDING_DATE_MODAL_ID,
} from "../components/UpdateSmsSendingDateModal.tsx";
import { BulkSelection } from "@/shared/components";

const DonationsPage = () => {
  const links = [{ name: "إسناد الطلبات", link: "" }];

  const {
    data,
    isLoading,
    error,
    totalItems,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    pg,
    updatePg,
    selectedIds,
    handleSelect,
    selectingAll,
    setSelectingAll,
    refetch,
    handleIgnoreFromVerification,
    isIgnoring,
    handleCreateManualVerification,
    isCreatingManual,
    clearSelection,
    selectionResetKey,
  } = useDonationsPage();

  const hasSelection = selectedIds.length > 0;

  return (
    <>
      <ErrorAlert reload={refetch} show={error} />
      <Breadcrumb links={links} />

      {/* Modal for assigning driver to order item */}
      <Modal fullHeight close={() => {}} title="اسناد الطلبات لمندوب">
        <AssignDriverModal
          ids={selectedIds}
          searchValues={search.values}
          selectingAll={selectingAll}
        />
      </Modal>

      {/* Modal for updating SMS sending date */}
      <Modal
        fullHeight
        id={UPDATE_SMS_SENDING_DATE_MODAL_ID}
        close={() => {}}
        title="تحديث تاريخ التنفيذ"
      >
        <UpdateSmsSendingDateModal
          ids={selectedIds}
          selectingAll={selectingAll}
          searchValues={search.values}
          onSuccess={clearSelection}
        />
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="العمليات / إسناد"
          title="إسناد الطلبات"
          count={totalItems}
          icon={TruckIcon}
          hideCount={isLoading}
        >
          <TableRowsPerPage
            rowsPerPageOptions={[5, 10, 50, 100, 300, 500, 1000]}
            pagination={{ ...pg, rows: totalItems }}
            onRowsPerPageChange={(size: number) => updatePg({ size, page: 1 })}
          />

          <ManualVerificationDropdown
            onSubmit={handleCreateManualVerification}
            isLoading={isCreatingManual}
          />
          <DownloadExcel
            length={data?.length}
            search={search.values}
            fetch={exportUnassignedVerificationStagesExcel}
            headerCellsName={donationsExcelHeaders}
          />
          <SearchToggle toggle={() => updateSearch({ show: !search.show })} />
        </PageHero>

        {/* Multi Search inputs */}
        <MultiSearch
          search={search}
          onSubmit={handleSearchSubmit}
          updateSearch={updateSearch}
          onReset={handleSearchReset}
        />

        {/* Action toolbar */}
        <div className="relative mt-5 rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-white p-2 dark:border-gray-700/60 dark:from-purple-950/20 dark:via-gray-800 dark:to-gray-800">
          {/* Selection state badge — own row on mobile, inline on lg */}
          {hasSelection && (
            <div className="mb-2 flex items-center gap-2 px-1 lg:mb-0 lg:hidden">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold tabular-nums text-purple-900 ring-1 ring-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIds.length)} محدد
              </span>
            </div>
          )}

          {/* Buttons grid: 2 cols on mobile, inline on lg+ */}
          <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center [&>*]:!w-full lg:[&>*]:!w-fit">
            {/* Inline selection badge for lg+ only */}
            {hasSelection && (
              <span className="!hidden lg:me-1 lg:!inline-flex lg:items-center lg:gap-1.5 lg:rounded-full lg:bg-purple-100 lg:px-2.5 lg:py-1 lg:text-xs lg:font-bold lg:tabular-nums lg:text-purple-900 lg:ring-1 lg:ring-purple-200 dark:lg:bg-purple-950/50 dark:lg:text-purple-300 dark:lg:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIds.length)} محدد
              </span>
            )}

            <ModalOpenBtn
              style="btn-green !w-full lg:!w-fit !justify-center"
              disabled={!hasSelection}
              title="إسناد لمندوب"
            />
            <Button
              type="button"
              className="btn-red !w-full !justify-center lg:!w-fit"
              isLoading={isIgnoring}
              title="إستبعاد من التوثيق"
              disabled={!hasSelection}
              onClick={handleIgnoreFromVerification}
            >
              <XCircleIcon className="h-5 w-5" />
            </Button>

            {/* Divider — desktop only */}
            <span
              aria-hidden
              className="!hidden lg:!block lg:h-7 lg:!w-px lg:bg-gradient-to-b lg:from-transparent lg:via-gray-200 lg:to-transparent dark:lg:via-gray-700"
            />

            <ModalOpenBtn
              variant="btn-outline-green"
              style="!w-full lg:!w-fit !justify-center"
              title="تحديث تاريخ التنفيذ"
              target={UPDATE_SMS_SENDING_DATE_MODAL_ID}
              disabled={!hasSelection}
            >
              <CalendarDaysIcon className="h-5 w-5" />
            </ModalOpenBtn>

            {/* Refresh — full width row on mobile, pushed to end on lg */}
            <button
              onClick={refetch}
              className="btn-gust col-span-2 inline-flex items-center justify-center gap-2 text-base lg:col-span-1 lg:ms-auto lg:!w-fit"
              title="تحديث"
            >
              <ArrowPathIcon className="h-5 w-5 transition-transform duration-500 hover:-rotate-180" />
              تحديث
            </button>
          </div>
        </div>

        {selectedIds.length === pg.size && (
          <BulkSelection
            selectedIds={selectedIds}
            totalItems={totalItems}
            clearSelection={clearSelection}
            selectingAll={selectingAll}
            setSelectingAll={setSelectingAll}
          />
        )}

        {/* Table */}
        <DataTable
          data={data}
          columns={tableColumns}
          isLoading={isLoading}
          pagination={{ ...pg, rows: totalItems }}
          onPageChange={(page: number) => updatePg({ page })}
          onPageSizeChange={(size: number) => updatePg({ size, page: 1 })}
          selectable
          onSelectedChange={handleSelect}
          selectionResetKey={selectionResetKey}
        />
      </main>
    </>
  );
};

export default DonationsPage;
